import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import axiosInstance from '../../lib/axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Plus, Trash2, Edit, Package } from 'lucide-react';
import { toast } from 'sonner';

export default function DepartmentsManagement() {
  const { user } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    hospital_id: '',
    name: '',
    description: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [deptRes, hospRes] = await Promise.all([
        axiosInstance.get('/departments'),
        axiosInstance.get('/hospitals')
      ]);
      setDepartments(deptRes.data);
      setHospitals(hospRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Auto-set hospital_id for hospital admins
      const submitData = { ...formData };
      if (user?.role === 'hospital_admin' && hospitals.length > 0 && !submitData.hospital_id) {
        submitData.hospital_id = hospitals[0].id;
      }
      
      if (editingId) {
        await axiosInstance.put(`/departments/${editingId}`, submitData);
        toast.success('Department updated');
      } else {
        await axiosInstance.post('/departments', submitData);
        toast.success('Department added');
      }
      setShowDialog(false);
      setEditingId(null);
      setFormData({ hospital_id: '', name: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (dept) => {
    setEditingId(dept.id);
    setFormData({
      hospital_id: dept.hospital_id,
      name: dept.name,
      description: dept.description
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this department?')) return;
    try {
      await axiosInstance.delete(`/departments/${id}`);
      toast.success('Department deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-manrope font-bold mb-2">Departments</h1>
            <p className="text-slate-600">Manage hospital departments</p>
          </div>
          
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditingId(null); setFormData({ hospital_id: '', name: '', description: '' }); }}>
                <Plus className="h-4 w-4 mr-2" />
                Add Department
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingId ? 'Edit' : 'Add'} Department</DialogTitle>
                <DialogDescription>Department details</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {user?.role === 'super_admin' && (
                  <div className="space-y-2">
                    <Label>Hospital</Label>
                    <Select value={formData.hospital_id} onValueChange={(v) => setFormData({...formData, hospital_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Select hospital" /></SelectTrigger>
                      <SelectContent>
                        {hospitals.map((h, i) => <SelectItem key={i} value={h.id}>{h.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} />
                </div>
                <div className="flex gap-2">
                  <Button type="submit">{editingId ? 'Update' : 'Add'}</Button>
                  <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid gap-4">
          {departments.map((dept, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Package className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{dept.name}</h3>
                      <p className="text-sm text-slate-600">{dept.description}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(dept)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => handleDelete(dept.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}